"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useState, useEffect, useRef } from "react";
import { AlignLeft, X, ArrowRight, MenuIcon } from "lucide-react";
import Image from "next/image";
import { Button } from "@/components/ui/button";

interface NavItem {
    name: string;
    href: string;
}

const navItems: NavItem[] = [
    { name: "Home", href: "#home" },
    { name: "About Me", href: "#about" },
    { name: "Services", href: "#services" },
    { name: "Events", href: "#events" },
    { name: "Why Me", href: "#why-choose" },
    { name: "FAQ", href: "#faq" },
];


export default function Nav() {
    const [isOpen, setIsOpen] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
    const navRef = useRef<HTMLUListElement>(null);
    const pathname = usePathname();
    const router = useRouter();

    const handleNavClick = (
        e: React.MouseEvent<HTMLAnchorElement>,
        href: string
    ) => {
        e.preventDefault();

        if (href.startsWith("#")) {
            const sectionId = href.substring(1);
            if (pathname === "/") {
                const element = document.getElementById(sectionId);
                if (element) {
                    const desktopNav = document.querySelector(".md\\:flex.hidden") as HTMLElement;
                    const mobileNav = document.querySelector(".md\\:hidden.flex") as HTMLElement;
                    let navHeight = 0;

                    if (window.innerWidth < 768 && mobileNav) {
                        navHeight = mobileNav.clientHeight;
                    } else if (desktopNav) {
                        navHeight = desktopNav.clientHeight;
                    }

                    const bodyRect = document.body.getBoundingClientRect().top;
                    const elementRect = element.getBoundingClientRect().top;
                    const elementPosition = elementRect - bodyRect;
                    const offsetPosition = elementPosition - navHeight;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: "smooth",
                    });
                }
            } else {
                router.push("/" + href);
            }
        } else {
            router.push(href);
        }

        if (isOpen) {
            setIsOpen(false);
        }
    };

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const handleMouseEnter = (
        index: number,
        event: React.MouseEvent<HTMLDivElement | HTMLAnchorElement>
    ) => {
        if (window.innerWidth > 768) {
            const target = event.currentTarget;
            const nav = navRef.current;

            if (nav && target) {
                const navRect = nav.getBoundingClientRect();
                const targetRect = target.getBoundingClientRect();

                setIndicatorStyle({
                    left: targetRect.left - navRect.left,
                    width: targetRect.width,
                });
                setHoveredIndex(index);
            }
        }
    };

    const handleMouseLeave = () => {
        if (window.innerWidth > 768) {
            setHoveredIndex(null);
        }
    };

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 768) {
                setIsOpen(false);
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const [touchedCardIndex, setTouchedCardIndex] = useState<number | null>(null);

    const handleTouchStart = (index: number) => {
        setTouchedCardIndex(index);
    };

    const handleTouchEnd = () => {
        setTouchedCardIndex(null);
    };



    return (
        <div className="fixed w-full top-0 z-50 pointer-events-none">
            <div className="md:flex hidden justify-end items-center h-[6vw] px-[5%]">
                {/* <Link href="/" className="mt-[8vw]">
                    <Image
                        src="/logo.webp"
                        alt="Logo"
                        width={300}
                        height={167}
                        className="w-[14vw] relative z-10"
                    />
                </Link> */}

                <div className="flex flex-row items-center justify-center gap-[2vw] pointer-events-auto">
                    <ul
                        ref={navRef}
                        className="flex relative items-center bg-white/20 backdrop-blur-md rounded-full h-[2.4vw]"
                        onMouseLeave={handleMouseLeave}
                    >
                        <li
                            className={`absolute h-full bg-gradient-to-r from-[#C5C5C5] to-[#F6F6FC] rounded-full ease-out drop-shadow-[0_0_2px_rgba(0,0,0,0.1)] group flex items-center ${hoveredIndex !== null ? "opacity-100" : "opacity-0"
                                }`}
                            style={{
                                left: `${indicatorStyle.left}px`,
                                width: `${indicatorStyle.width}px`,
                                transition: "opacity 200ms ease-out, left 200ms ease-out, width 200ms ease-out"
                            }}
                        />

                        {navItems.map((item, index) => {
                            return (
                                <li
                                    key={item.name}
                                    className="relative group flex items-center"
                                >
                                    <Link
                                        href={item.href}
                                        onClick={(e) => {
                                            handleNavClick(e, item.href);
                                        }}
                                        className={`relative z-10 text-[1.1vw] px-[1.5vw] rounded-full transition-all duration-300 whitespace-nowrap block flex items-center
                                            ${(pathname === "/" && item.href === "/") ||
                                                (pathname === item.href && item.href !== "/")
                                                ? "bg-gradient-to-r from-[#C5C5C5] to-[#F6F6FC] h-full drop-shadow-[0_0_2px_rgba(0,0,0,0.1)]"
                                                : "text-[#E2E8F0] group-hover:text-black font-semibold"
                                            }
                                        `}
                                        onMouseEnter={(e) => handleMouseEnter(index, e)}
                                    >
                                        {item.name}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>

                    <div className="flex items-center gap-[0.5vw]">
                        <Link href="https://www.instagram.com/dj_sachin_2023/" className="cursor-pointer" target="_blank" aria-label="Visit DJ Sachin's Instagram page">
                            <Image src={"/insta.svg"} alt="Instagram" width={100} height={100} className="w-[2vw] h-[2vw] transition-all duration-300 ease-in hover:drop-shadow-[0_0_5px_#FF004D] bg-transparent" />
                        </Link>

                        {/* <Link href="#" className="cursor-pointer" >
                            <Image src={"/yt.svg"} alt="Youtube" width={100} height={100} className="w-[2vw] h-[2vw] transition-all duration-300 ease-in hover:drop-shadow-[0_0_5px_#FF004D] bg-transparent" />
                        </Link> */}

                        <Link href="https://www.facebook.com/share/1E2bWfzme6/?mibextid=wwXIfr" className="cursor-pointer" target="_blank" aria-label="Visit DJ Sachin's Facebook page">
                            <Image src={"/fb.svg"} alt="Facebook" width={100} height={100} className="w-[2vw] h-[2vw] transition-all duration-300 ease-in hover:drop-shadow-[0_0_5px_#FF004D] bg-transparent" />
                        </Link>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            <div className="md:hidden flex items-center justify-between h-20 px-[5.5%] relative -z-50 pointer-events-auto"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.2)" }}>


                <Link href="/" className="">
                    <Image
                        src="/logo.webp"
                        alt="Logo for MV"
                        width={300}
                        height={167}
                        className="w-[36vw] h-auto max-w-[100px]"
                    />
                </Link>

                <Button
                    onClick={toggleMenu}
                    className="w-8 h-8 text-white transition-all duration-300 z-50 flex items-center justify-center shadow-none"
                    aria-label="Toggle menu"
                >
                    <div className="relative">
                        <MenuIcon
                            className={`transition-all duration-300 text-white
                                ${isOpen
                                    ? "opacity-0 scale-0"
                                    : "opacity-100 scale-100"
                                }`}
                        />
                    </div>
                </Button>
            </div>

            <div className={`md:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 pointer-events-auto ${isOpen ? "opacity-100 visible" : "opacity-0 invisible"
                }`}
                onClick={() => setIsOpen(false)}
            />

            <div
                className={`md:hidden fixed left-0 top-0 h-full w-full bg-black/90 shadow-xl z-50 transform transition-transform duration-300 ease-in-out pointer-events-auto ${isOpen ? "translate-x-0" : "-translate-x-full"
                    }`}
            >
                <div className="p-8 pb-5 pt-3 mt-4 flex items-center justify-between">

                    <Link href="/">
                        <Image
                            src="/logo.webp"
                            alt="Logo MV"
                            width={300}
                            height={167}
                            className="w-40 relative z-50"
                        />
                    </Link>
                    <Button
                        onClick={() => setIsOpen(false)}
                        className="w-10 h-10 text-white hover:bg-[#141829] transition-colors duration-200"
                    >
                        <X />
                    </Button>
                </div>
                <div className="w-full h-[1px] bg-gray-300/30" />

                <div className="p-4">
                    <ul className="space-y-2">
                        {navItems.map((item, index) => {
                            return (
                                <li key={item.name}>
                                    <Link
                                        href={item.href}
                                        onClick={(e) => {
                                            handleNavClick(e, item.href);
                                        }}
                                        className={`px-4 py-2 transition-all duration-200 transform text-lg flex items-center justify-between
                                                ${isOpen
                                                ? "translate-x-0 opacity-100"
                                                : "translate-x-[-20px] opacity-0"}
                                                ${(pathname === "/" && item.href === "/") ||
                                                (pathname === item.href && item.href !== "/")
                                                ? "bg-white text-black font-semibold shadow rounded-lg"
                                                : "text-[#E2E8F0] hover:bg-white rounded-2xl hover:text-black"
                                            }`}
                                        style={{
                                            transitionDelay: isOpen ? `${index * 50}ms` : "0ms",
                                        }}
                                    >
                                        {item.name}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </div>
            </div>
        </div>
    );
}
